import Fuse from 'fuse.js';

/**
 * Normalizes a string by removing accents and converting to lowercase.
 * This helps Fuse.js match strings like "Bebida" with "bebída".
 */
export const normalizeString = (str: string): string => {
    return str
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
};

/**
 * Options for fuzzy search.
 */
export interface FuzzySearchOptions<T> {
    keys: string[];
    threshold?: number;
    includeScore?: boolean;
}

/**
 * Performs a fuzzy search on an array of items.
 * @param list The array to search in.
 * @param searchTerm The term to search for.
 * @param options Fuse.js options (keys to search, threshold, etc.).
 * @returns The filtered items.
 */
export const fuzzySearch = <T>(
    list: T[],
    searchTerm: string,
    options: FuzzySearchOptions<T>
): T[] => {
    if (!searchTerm) return list;

    const fuseOptions = {
        ...options,
        threshold: options.threshold ?? 0.35, // 0.0 is exact match, 1.0 is everything
        ignoreLocation: true,
        useExtendedSearch: true,
        getFn: (obj: any, path: string | string[]) => {
            const value = Fuse.config.getFn(obj, path);
            if (typeof value === 'string') {
                return normalizeString(value);
            }
            return value;
        }
    };

    const fuse = new Fuse(list, fuseOptions);
    const normalizedTerm = normalizeString(searchTerm);

    return fuse.search(normalizedTerm).map(result => result.item);
};
